import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layers } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';
import ProgressiveImage from '@/components/ProgressiveImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Collections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/collections?include_artworks=true`)
      .then(res => {
        setCollections(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO title="Art Collections - Gangadhar Som | Curated Painting Series" description="Browse curated collections of artwork by Gangadhar Som. Themed series featuring Krishna, devotional art, mythology, and more." url={`${window.location.origin}/collections`} />
      <div className="min-h-screen pt-24 sm:pt-32 pb-16 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="collections-page">
        <div className="max-w-screen-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="mb-10 sm:mb-14"
          >
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light mb-3">Collections</h1>
            <p className="text-muted-foreground text-sm sm:text-base max-w-xl">
              Curated series of paintings, grouped by theme, style, and story.
            </p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="aspect-[4/3] bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : collections.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <Layers size={48} className="mx-auto mb-4 opacity-40" />
              <p className="text-lg">No collections yet</p>
              <p className="text-sm mt-1">Check back soon for curated artwork series.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {collections.map((collection, index) => (
                <motion.div
                  key={collection.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/collections/${collection.id}`}
                    className="group block"
                    data-testid={`collection-card-${collection.id}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-muted mb-3">
                      {/* Mosaic preview from first 4 artworks */}
                      {collection.artworks && collection.artworks.length > 0 ? (
                        <div className="grid grid-cols-2 grid-rows-2 w-full h-full">
                          {collection.artworks.slice(0, 4).map((art, i) => (
                            <div key={art.id} className="overflow-hidden">
                              <ProgressiveImage
                                src={art.thumb_url || art.image_url}
                                placeholder={art.placeholder}
                                alt={art.title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                            </div>
                          ))}
                          {/* Fill empty slots */}
                          {Array.from({ length: Math.max(0, 4 - (collection.artworks?.length || 0)) }).map((_, i) => (
                            <div key={`empty-${i}`} className="bg-muted/50" />
                          ))}
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Layers size={40} className="text-muted-foreground/30" />
                        </div>
                      )}
                      {/* Overlay gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                      {/* Collection name on card */}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h2 className="text-white font-serif text-xl sm:text-2xl font-light">{collection.name}</h2>
                        <p className="text-white/70 text-xs mt-1">
                          {collection.artwork_ids?.length || 0} artwork{(collection.artwork_ids?.length || 0) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    {collection.description && (
                      <p className="text-muted-foreground text-sm line-clamp-2">{collection.description}</p>
                    )}
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Collections;
